import { focusManager, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { Toaster } from 'sonner'
import { createQueryClient } from '@/api/query-client'
import { AuthProvider } from '@/auth/AuthProvider'
import { FullPageMessage } from '@/components/FullPageMessage'
import { TooltipProvider } from '@/components/ui/tooltip'
import { getConfig } from '@/lib/config'
import { ThemeProvider, useTheme } from '@/lib/theme'
import { createAppRouter } from './router'
import './styles/app.css'

const queryClient = createQueryClient()
const router = createAppRouter(queryClient)

if (import.meta.env.DEV) {
  // Debug handles for browser devtools and automated visual checks.
  Object.assign(window, { __mailsmith: { queryClient, router, focusManager } })
}

function ThemedToaster() {
  const { resolved } = useTheme()
  return (
    <Toaster
      theme={resolved}
      position="bottom-right"
      closeButton
      toastOptions={{
        classNames: {
          toast: 'font-sans! rounded-lg! border-line! shadow-pop! pr-10!',
          description: 'whitespace-pre-line',
          // Inside the toast's top-right corner instead of floating over its edge.
          closeButton:
            'left-auto! right-2! top-2! transform-none! border-0! bg-transparent! text-ink-3! hover:bg-surface-3! hover:text-ink!',
        },
      }}
    />
  )
}

function App() {
  // Fail loudly on bad runtime config instead of a blank page.
  try {
    getConfig()
  } catch (err) {
    return (
      <FullPageMessage
        title="MailSmith isn’t configured"
        description={err instanceof Error ? err.message : String(err)}
      />
    )
  }
  return (
    <AuthProvider>
      <RouterProvider router={router} />
    </AuthProvider>
  )
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider delayDuration={300} skipDelayDuration={150}>
          <App />
          <ThemedToaster />
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
)
